import React, { useState, useEffect } from 'react';
import { useTranslate, useTranslationStatus } from '../hooks/useTranslate';
import { useLanguage } from '../context/LanguageContext';
import { OptimizedImage } from '../components/ui/optimized-image';

const Association = () => {
  const [headerImageUrl, setHeaderImageUrl] = useState(null);
  const [pageContent, setPageContent] = useState('');
  const [loading, setLoading] = useState(true);
  const { currentLanguage } = useLanguage();
  
  // Savoir si des traductions sont en cours
  const isTranslating = useTranslationStatus();

  // Traductions des textes statiques
  const { translatedText: titleTranslated } = useTranslate("L'Association");
  const { translatedText: subtitleTranslated } = useTranslate("Les Lignages de Bruxelles");
  const { translatedText: descTranslated } = useTranslate("Découvrez notre association et ses missions");
  
  // Traduction du contenu dynamique - SEULEMENT si pageContent existe
  const { translatedText: contentTranslated } = useTranslate(pageContent || "");

  useEffect(() => {
    // Récupérer le contenu ET l'image header de la page depuis l'API
    const fetchPageData = async () => {
      try {
        console.log('🔄 Chargement des données de la page Association...');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/pages/association`);
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Données Association chargées:', data);
          
          // Récupérer le contenu
          if (data.content) {
            setPageContent(data.content);
          }
          
          // Récupérer l'image header depuis la base de données
          if (data.header_image) {
            // Si c'est déjà une URL complète
            if (data.header_image.startsWith('http') || data.header_image.startsWith('/api')) {
              setHeaderImageUrl(`${process.env.REACT_APP_BACKEND_URL}${data.header_image.startsWith('/') ? data.header_image : '/' + data.header_image}`);
            } else {
              setHeaderImageUrl(data.header_image);
            }
            console.log('✅ Image header chargée:', data.header_image);
          } else {
            // Fallback : image par défaut
            setHeaderImageUrl(`${process.env.REACT_APP_BACKEND_URL}/images/lignages-bruxelles.jpg`);
          }
        }
      } catch (error) {
        console.error('Erreur chargement page association:', error);
        // Fallback en cas d'erreur
        setHeaderImageUrl(`${process.env.REACT_APP_BACKEND_URL}/images/lignages-bruxelles.jpg`);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  // Ne plus afficher de spinner bloquant - afficher la page directement
  return (
    <div className="min-h-screen bg-white">
      {/* Header image section */}
      {headerImageUrl && (
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            height: '600px',
            marginTop: '-80px',
            paddingTop: '80px'
          }}
        >
          <OptimizedImage 
            src={headerImageUrl}
            alt="L'Association"
            className="absolute inset-0 w-full h-full object-cover"
            priority={true}
            placeholder="blur"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
            <h1 
              className="text-5xl font-bold text-center px-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)',
                textShadow: '3px 3px 6px rgba(0,0,0,0.8), -1px -1px 2px rgba(255,255,255,0.3)'
              }}
            >
              {titleTranslated}
            </h1>
            <p 
              className="text-2xl text-center px-4 mt-3"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              {subtitleTranslated}
            </p>
          </div>
        </div>
      )}

      <div className="py-16 bg-gray-50" style={{ paddingTop: headerImageUrl ? '4rem' : '8rem' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Alerte de traduction en cours - SOUS LE HEADER avec z-index élevé */}
          {isTranslating && currentLanguage !== 'fr' && (
            <div 
              className="mb-6 bg-blue-50 border-l-4 border-blue-600 text-blue-800 px-6 py-4 rounded-r-lg shadow-md flex items-center gap-3"
              style={{ fontFamily: '"Lora", serif', position: 'relative', zIndex: 10 }}
            >
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <div>
                <p className="font-semibold">Traduction en cours...</p>
                <p className="text-sm">Le contenu s'affiche en français en attendant la traduction complète.</p>
              </div>
            </div>
          )}
          
          {/* Header sans image */}
          {!headerImageUrl && (
            <div className="text-center mb-12">
              <h1 
                className="text-5xl font-bold mb-4"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {titleTranslated}
              </h1>
            </div>
          )}

          {/* Contenu de la page (éditable via admin) */}
          {pageContent && (
            <div 
              dangerouslySetInnerHTML={{ 
                __html: currentLanguage === 'fr' 
                  ? pageContent 
                  : (contentTranslated || pageContent) 
              }} 
            />
          )}
          
          {/* Si pas de contenu, afficher un message */}
          {!pageContent && !loading && (
            <div className="text-center text-gray-500 py-12">
              <p style={{ fontFamily: '"Lora", serif' }}>Aucun contenu disponible pour cette page.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Association;
